import { Box, Link, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { blue, green } from "@mui/material/colors";
import { MetricsContext, TimeFrame } from "contexts/Metrics";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ActionItem } from "schemas/dashboard";
import { routes } from "schemas/routes";

import Metric from "features/Home/Sidebar/MetricsHelper/Metric";
import MetricsByTemplate from "features/Home/Sidebar/MetricsHelper/Template";
import { TimePeriod } from "features/Home/Sidebar/MetricsHelper/schema";
import { trackEvent } from "utils/tracking";

interface Props {
  actionItems: ActionItem[];
}

export default function MetricsHome({ actionItems }: Props) {
  const { metrics, templateStats, fetchMetrics } = useContext(MetricsContext);

  const [tab, setTab] = useState(TimeFrame.monthly);

  const onTabChange = (_: React.SyntheticEvent, newValue: TimeFrame) => {
    trackEvent("Home Page Metrics Time Frame Changed", {
      "Time Frame": newValue,
    });
    setTab(newValue);

    if (newValue === TimeFrame.daily) {
      fetchMetrics(TimePeriod.today, true);
    } else if (
      newValue === TimeFrame.weekly &&
      metrics[TimePeriod.thisWeek] === undefined
    ) {
      fetchMetrics(TimePeriod.thisWeek, true);
    }
  };

  useEffect(() => {
    fetchMetrics(TimePeriod.thisMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typography paragraph variant="h4">
        <Box
          component="i"
          className="fa-solid fa-chart-simple"
          sx={{ pr: 1, color: "secondary.main" }}
        />{" "}
        Metrics
      </Typography>
      {metrics[TimePeriod.thisMonth]?.outreachCount !== undefined ? (
        <Typography paragraph>
          <>
            You've sent{" "}
            <Typography component="span" sx={{ color: green[500] }}>
              <strong>
                {Math.round(metrics[TimePeriod.thisMonth].outreachCount)}
              </strong>
            </Typography>{" "}
            emails this month!{" "}
            {actionItems?.length > 0 && (
              <>
                {" "}
                <Link
                  component={RouterLink}
                  to={`/${routes.brands}/all`}
                  sx={{ color: blue[500], textDecoration: "none" }}
                >
                  <strong>Send more emails</strong>
                </Link>{" "}
                and you will likely land more brand deals.
              </>
            )}
          </>
        </Typography>
      ) : (
        <Skeleton height="35px" />
      )}
      <Tabs onChange={onTabChange} value={tab}>
        <Tab label="Daily" value={TimeFrame.daily} />
        <Tab label="Weekly" value={TimeFrame.weekly} />
        <Tab label="Monthly" value={TimeFrame.monthly} />
      </Tabs>
      <Box sx={{ mb: 4 }}>
        <Metric timeFrame={tab} />
      </Box>
      {templateStats?.length > 0 && (
        <Typography paragraph variant="h4">
          <Box
            component="i"
            className="fa-solid fa-file-pen"
            sx={{ pr: 1, color: "secondary.main" }}
          />{" "}
          Templates
        </Typography>
      )}
      <Box sx={{ mb: 4, maxWidth: 750 }}>
        <MetricsByTemplate />
      </Box>
    </>
  );
}
